import { X, Maximize2, Minimize2, Pencil, Share2, Download, Printer, FileQuestion } from 'lucide-react';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { SigningPanel } from '@/modules/signing/components/SigningPanel';
import { AgreementPanel } from '@/modules/signing/components/AgreementPanel';
import { CollaboraEditor } from '@/modules/collabora-editor/index.jsx';
import LoadingSpinner from '@/shared/ui/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { TruncatedTooltip } from '@/shared/ui/TruncatedTooltip.jsx';
import { TextPreview } from '@/shared/ui/TextPreview.jsx';
import { fileKind, isCollaboraEditable } from '@/shared/utils/fileKind.js';
import { can, lockOf } from '@/shared/utils/nodeAccess.js';
import { FileStateBanner } from '@/shared/ui/FileStateBanner.jsx';
import { toast } from 'sonner';

export function DocumentModal({ open, onClose, doc, onEdit, onShare }) {
  const { t } = useTranslation(['common', 'signing', 'editor']);
  const preview = doc?.previewUrl;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const modalRef = useRef(null);

  // Esc closes the preview (this is a custom modal, not Radix, so it doesn't
  // get Esc-to-close for free). If fullscreen, Esc exits that first. Bind on
  // document with capture, and focus the modal so it reliably receives keys
  // even inside the embed iframe.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (isFullscreen) setIsFullscreen(false);
      else onClose?.();
    };
    document.addEventListener('keydown', onKey, true);
    const id = requestAnimationFrame(() => modalRef.current?.focus());
    return () => {
      document.removeEventListener('keydown', onKey, true);
      cancelAnimationFrame(id);
    };
  }, [open, isFullscreen, onClose]);

  // One shared classifier (src/shared/utils/fileKind.js) decides how to render.
  const extension = (doc?.extension || '').toLowerCase();
  const kind = useMemo(() => fileKind(doc), [doc]);
  // Office also covers real word/sheet/presentation MIME strings that aren't a
  // bare extension (fileKind only knows extensions + image/pdf MIMEs).
  const isOfficeDoc = kind === 'office'
    || /word|sheet|excel|ms-?excel|presentation|powerpoint|opendocument/.test(extension);
  const isImage = kind === 'image';
  const isPdf = kind === 'pdf'
    || preview?.split('.').pop()?.split('?')[0]?.toLowerCase() === 'pdf';
  const isText = kind === 'text';

  // Anything Collabora can open for editing — office formats (Writer, Calc,
  // Impress) plus txt. Other text-kind files (yml/json/md…) are view-only:
  // Collabora would open them read-only, so no pencil for them.
  const isEditableDoc = isCollaboraEditable(doc);

  // Get JSON content from the document object directly
  const docJsonContent = doc?.contentJson || doc?.content_json || null;

  // Can we render this inline? If not, the body shows an "unsupported" notice
  // with its own download button, and the header download/print are hidden.
  const canPreview = isOfficeDoc || isImage || isPdf || isText;
  // A share with download & print off: the buttons go (the editor hides its
  // own print/export too). It discourages copying, it can't prevent it.
  const mayDownload = can(doc, 'download');
  // Someone holds the file (e.g. while it is reviewed): say who, and why.
  const lock = lockOf(doc);

  // Determine right-side panel based on document type
  const docType = doc?.approvalState || doc?.approval_state;
  const isAgreementDoc = docType === 'agreement';
  const isSignedDoc = docType === 'signed';
  const showSigningPanel = isSignedDoc;
  const showAgreementPanel = isAgreementDoc;
  const showSidePanel = showSigningPanel || showAgreementPanel;

  // State for PDF blob URL
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Load PDF as blob to bypass X-Frame-Options
  useEffect(() => {
    if (!open || !isPdf || !preview) {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }
      return;
    }

    setPdfLoading(true);
    const fetchPdfBlob = async () => {
      try {
        const response = await fetch(preview);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(blobUrl);
      } catch (error) {
        console.error('Failed to load PDF:', error);
        setPdfBlobUrl(null);
      } finally {
        setPdfLoading(false);
      }
    };

    fetchPdfBlob();

    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [open, isPdf, preview]);

  // Escape key closes modal (unless in fullscreen)
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape' && !document.fullscreenElement) onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  // Sync isFullscreen with actual browser fullscreen state
  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    document.addEventListener('webkitfullscreenchange', handleFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFsChange);
      document.removeEventListener('webkitfullscreenchange', handleFsChange);
    };
  }, []);

  // Exit fullscreen when modal closes
  useEffect(() => {
    if (!open && document.fullscreenElement) {
      document.exitFullscreen().catch(() => { });
    }
  }, [open]);

  // Toggle fullscreen with browser API
  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await modalRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.warn('Fullscreen error:', err);
    }
  }, []);

  // Download file
  const handleDownload = useCallback(async () => {
    if (!preview) {
      toast.error(t('editor:noFileToDownload'));
      return;
    }
    setIsDownloading(true);
    try {
      const response = await fetch(preview);
      if (!response.ok) throw new Error(t('editor:downloadFailed'));
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc?.name || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(t('actions.downloadError'));
    } finally {
      setIsDownloading(false);
    }
  }, [preview, doc, t]);

  // Print via a hidden same-origin iframe pointed at a blob: URL. Never
  // window.open(preview) — the presigned URL serves Content-Disposition:
  // attachment, so the browser downloads instead of printing.
  const printBlobUrl = useCallback((url, revokeAfter) => {
    const iframe = document.createElement('iframe');
    iframe.style.visibility = 'hidden';
    iframe.style.position = 'fixed';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.zIndex = '-1';
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        if (revokeAfter) URL.revokeObjectURL(url);
        setIsPrinting(false);
      }, 1000);
    };
  }, []);

  // Print file
  const handlePrint = useCallback(async () => {
    // Office previews render in Collabora — it prints natively (renders to
    // PDF and opens the print dialog) via the WOPI PostMessage API, sent
    // through the editor's sendCommand so it names the Collabora origin.
    if (isOfficeDoc) {
      const frame = modalRef.current?.querySelector('.collabora-frame');
      if (frame?._collabora) {
        frame._collabora.sendCommand({ MessageId: 'Action_Print' });
      } else {
        toast.error(t('editor:printNotReady'));
      }
      return;
    }

    if (!preview) {
      toast.error(t('editor:nothingToPrint'));
      return;
    }
    setIsPrinting(true);

    if (isImage) {
      // Image: open in new window and print
      const win = window.open('', '_blank');
      win.document.write(`
        <html><body style="margin:0;display:flex;align-items:center;justify-content:center;">
          <img src="${preview}" style="max-width:100%;max-height:100vh;object-fit:contain;" onload="window.print();window.close();" />
        </body></html>
      `);
      win.document.close();
      setIsPrinting(false);
    } else if (isPdf && pdfBlobUrl) {
      printBlobUrl(pdfBlobUrl, false);
    } else if (isText) {
      // Text: fetch the content and print it as a rendered page
      try {
        const text = await (await fetch(preview)).text();
        const escaped = text
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const blob = new Blob(
          [`<html><body><pre style="white-space:pre-wrap;font-family:monospace;font-size:12px;margin:16px;">${escaped}</pre></body></html>`],
          { type: 'text/html' },
        );
        printBlobUrl(URL.createObjectURL(blob), true);
      } catch {
        toast.error(t('editor:printFailed'));
        setIsPrinting(false);
      }
    } else {
      // PDF whose blob hasn't loaded yet (or other previewables): fetch to a
      // blob first, then print through the hidden iframe.
      try {
        const blob = await (await fetch(preview)).blob();
        printBlobUrl(URL.createObjectURL(blob), true);
      } catch {
        toast.error(t('editor:printFailed'));
        setIsPrinting(false);
      }
    }
  }, [preview, isOfficeDoc, isPdf, pdfBlobUrl, isImage, isText, printBlobUrl, t]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[50] bg-black/60 dark:bg-black/80 flex items-center justify-center p-0 sm:p-4 transition-opacity duration-300 backdrop-blur-sm dark:backdrop-blur-md">
      <div
        ref={modalRef}
        tabIndex={-1}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); if (isFullscreen) setIsFullscreen(false); else onClose?.(); } }}
        className="bg-white dark:bg-[#0f1112] w-full h-full sm:w-[95vw] sm:h-[90vh] sm:rounded-xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 dark:border-white/5 outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between bg-white dark:bg-[#1a1c1e] text-gray-900 dark:text-white border-b border-gray-100 dark:border-white/5 px-3 sm:px-6 h-[64px] flex-shrink-0">
          <TruncatedTooltip content={doc.name} className="max-w-[50vw]">
            <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-white/90">{doc.name}</h2>
          </TruncatedTooltip>

          <div className="flex items-center gap-1">
            {/* Fullscreen */}
            <button
              onClick={toggleFullscreen}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-white/60 hover:text-gray-900 dark:hover:text-white transition-all duration-200 cursor-pointer"
              title={isFullscreen ? t('actions.exitFullscreen') : t('actions.fullscreen')}
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>

            {/* Edit — any Collabora-editable format */}
            {onEdit && isEditableDoc && (
              <button
                onClick={onEdit}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-white/60 hover:text-gray-900 dark:hover:text-white transition-all duration-200 cursor-pointer"
                title={t('actions.edit')}
              >
                <Pencil className="w-5 h-5" />
              </button>
            )}

            {/* Share */}
            {onShare && (
              <button
                onClick={onShare}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-white/60 hover:text-gray-900 dark:hover:text-white transition-all duration-200 cursor-pointer"
                title={t('actions.share')}
              >
                <Share2 className="w-5 h-5" />
              </button>
            )}

            {/* Download + Print — only when the file is actually previewable.
                For unsupported files the body has its own download button and
                printing is meaningless. */}
            {canPreview && mayDownload && (
              <button
                onClick={handleDownload}
                disabled={isDownloading || !preview}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-white/60 hover:text-gray-900 dark:hover:text-white transition-all duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                title={t('actions.download')}
              >
                {isDownloading
                  ? <LoadingSpinner width={20} color="currentColor" />
                  : <Download className="w-5 h-5" />
                }
              </button>
            )}

            {canPreview && mayDownload && (
              <button
                onClick={handlePrint}
                disabled={isPrinting || (!isOfficeDoc && !preview && !docJsonContent)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-white/60 hover:text-gray-900 dark:hover:text-white transition-all duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                title={t('actions.print')}
              >
                <Printer className="w-5 h-5" />
              </button>
            )}

            <div className="h-6 w-px bg-gray-200 dark:bg-white/10 mx-1" />

            {/* Close */}
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-red-50 dark:hover:bg-red-500/20 text-gray-500 dark:text-white/60 hover:text-red-500 dark:hover:text-red-400 transition-all duration-200 cursor-pointer"
              title={t('actions.close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {lock && <FileStateBanner reason="locked" lock={lock} />}

        {/* Body — column on phones (preview stacked over the side panel),
            side-by-side from sm up. */}
        <div className="flex-1 bg-gray-50 dark:bg-[#0f1112] flex flex-col sm:flex-row overflow-hidden">
          {/* Document Preview */}
          <div className={`flex-1 overflow-hidden relative ${showSidePanel ? 'border-r border-gray-200 dark:border-white/5' : ''}`}>
            {isOfficeDoc ? (
              <div className="h-full bg-[#f8f9fa] dark:bg-[#1e1e1e] overflow-hidden">
                <CollaboraEditor
                  fileId={doc.id}
                  readonly={true}
                  onReady={() => {}}
                  onClose={onClose}
                />
              </div>
            ) : isImage && preview ? (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-[#1a1c1e] p-4">
                <img
                  src={preview}
                  alt={doc.name}
                  className="max-w-full max-h-full object-contain shadow-lg dark:shadow-2xl"
                  style={{ maxHeight: 'calc(90vh - 128px)' }}
                />
              </div>
            ) : isPdf && preview ? (
              <div className="w-full h-full bg-gray-100 dark:bg-[#1a1c1e]">
                {pdfLoading ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <LoadingSpinner width={32} color="#4C78DF" />
                  </div>
                ) : pdfBlobUrl ? (
                  <embed
                    src={pdfBlobUrl}
                    type="application/pdf"
                    className="w-full h-full dark:invert-[0.9] dark:hue-rotate-180"
                    title={doc.name}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center p-8">
                      <p className="text-lg font-medium text-gray-700 dark:text-white/80 mb-4">
                        {t('editor:pdfLoadFailed')}
                      </p>
                      <Button
                        className="bg-brand hover:bg-[#324E8C] text-white"
                        onClick={() => window.open(preview, '_blank')}
                      >
                        {t('editor:openInNewTab')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : isText && preview ? (
              <div className="w-full h-full bg-white dark:bg-[#1a1c1e]">
                <TextPreview url={preview} className="w-full h-full" />
              </div>
            ) : (
              /* Unsupported file type: don't auto-download or render garbage —
                 warn and let the user choose to download. */
              <div className="w-full h-full flex items-center justify-center p-8">
                <div className="text-center max-w-sm">
                  <FileQuestion className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-white/20" />
                  <p className="text-base font-medium text-gray-700 dark:text-white/80 mb-1">
                    {t('viewer.unsupportedTitle')}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-white/50 mb-5">
                    {t('viewer.unsupportedDesc')}
                  </p>
                  {mayDownload && <Button
                    onClick={handleDownload}
                    disabled={isDownloading || !preview}
                    className="bg-brand hover:bg-[#324E8C] text-white"
                  >
                    {isDownloading
                      ? <LoadingSpinner width={18} color="#fff" />
                      : <><Download className="w-4 h-4 mr-2" /> {t('actions.download')}</>}
                  </Button>}
                </div>
              </div>
            )}
          </div>

          {/* Signing Panel (right side) - only for E-IMZO documents */}
          {showSigningPanel && (
            <div className="w-full sm:w-[380px] shrink-0 max-h-[45%] border-t sm:max-h-none sm:border-t-0 bg-white dark:bg-[#1a1c1e] overflow-y-auto border-l border-gray-100 dark:border-white/5 animate-in slide-in-from-right duration-300">
              <div className="p-6">
                <h3 className="text-sm dark:text-[10px] font-semibold dark:font-bold text-gray-400 dark:text-white/40 uppercase tracking-wider dark:tracking-[0.2em] mb-6 dark:mb-8">
                  {t('signing:panelTitle')}
                </h3>
                <div className="dark:text-white">
                  <SigningPanel
                    fileId={doc.id}
                    documentId={doc.documentId || doc.document_id}
                    approvalState={docType}
                    document={doc}
                    onSigningComplete={() => { }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Agreement Panel (right side) - for non-cryptographic agreement docs */}
          {showAgreementPanel && (
            <div className="w-full sm:w-[380px] shrink-0 max-h-[45%] border-t sm:max-h-none sm:border-t-0 bg-white dark:bg-[#1a1c1e] overflow-y-auto border-l border-gray-100 dark:border-white/5 animate-in slide-in-from-right duration-300">
              <AgreementPanel fileId={doc.id} document={doc} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
